import React, { useCallback, useId, useLayoutEffect, useRef, useState } from 'react'

import { PlotRenderMode } from '@digital-twins/slide-schemas/generated/plot-types'

/**
 * "Other" opens in interactive views (plots plan OT, decision 23).
 *
 * When a rollup folded categories into "Other", an interactive view shows a
 * text link under the plot ("Show all (N)" / "Show less"). Opened, the plot
 * draws every folded category Python stamped in place of the Other mark, and
 * the plot box keeps the height it had closed: the content scrolls inside it.
 *
 * The link is drawn only when all three hold, so a static render (PDF, deck
 * image, visual QA baselines) and any host that passes no words draw exactly
 * what they drew before:
 * 1. the host renders interactively (`renderMode="interactive"`);
 * 2. the host passes the words (`OtherToggleLabels`, the viewer's UI language);
 * 3. the payload stamps what Other folded. Nothing is computed here.
 */

export interface OtherToggleLabels {
  /** The closed link for `count` categories (drawn plus folded), e.g. "Show all (12)": the host's i18n owns the wording. */
  expand: (count: number) => string
  /** The opened link, e.g. "Show less". */
  collapse: string
}

export function isOtherExpandable(
  renderMode: PlotRenderMode | undefined,
  labels: OtherToggleLabels | null | undefined,
  hasFolded: boolean,
): boolean {
  return renderMode === 'interactive' && Boolean(labels) && hasFolded
}

export interface OtherExpandState {
  isOpen: boolean
  toggle: () => void
  /** The plot box: measured once on open, then held at that height. */
  boxRef: React.RefObject<HTMLDivElement>
  /** The box's style while open (height lock and scroll); undefined when closed. */
  boxStyle: React.CSSProperties | undefined
  /** The box's id, named by the link's `aria-controls`. */
  boxId: string
}

export interface OtherExpandOptions {
  /**
   * Free room the box may grow into when it opens (px), measured once on open:
   * e.g. the space between a slide's rows and its bottom-anchored legend. The
   * box then holds between its closed height and closed height plus that room.
   */
  growRoom?: () => number
}

/** Opened members this far below the box top keep the row they open from in view (px). */
const REVEAL_CONTEXT = 48

/**
 * Free room under the last child of the box's parent, a fixed-height column
 * (a slide panel): what an opened box may grow into, in layout px (the deck
 * viewer's scale transform divided out). Zero where nothing is laid out.
 */
export function freeRoomInParent(box: HTMLElement | null): number {
  const parent = box?.parentElement
  const last = parent?.lastElementChild
  if (!parent || !last) {
    return 0
  }
  const parentRect = parent.getBoundingClientRect()
  const scale = parent.offsetHeight > 0 && parentRect.height > 0 ? parentRect.height / parent.offsetHeight : 1
  const padding = Number.parseFloat(getComputedStyle(parent).paddingBottom) || 0
  return (parentRect.bottom - last.getBoundingClientRect().bottom) / scale - padding
}

/**
 * Scrolls an opened box so the members it revealed show: all of them when
 * they fit, else from the top with the row they open from above them. The
 * members are the box's `<prefix>-other-member-N` elements; nothing is measured
 * when none is laid out (a static render never opens).
 */
function revealMembers(box: HTMLElement) {
  const members = Array.from(box.querySelectorAll<HTMLElement>('[data-testid*="-other-member-"]'))
    .map((element) => element.getBoundingClientRect())
    .filter((rect) => rect.height > 0)
  if (members.length === 0) {
    return
  }
  const boxRect = box.getBoundingClientRect()
  const scale = box.offsetHeight > 0 && boxRect.height > 0 ? boxRect.height / box.offsetHeight : 1
  const top = (Math.min(...members.map((rect) => rect.top)) - boxRect.top) / scale + box.scrollTop
  const bottom = (Math.max(...members.map((rect) => rect.bottom)) - boxRect.top) / scale + box.scrollTop
  box.scrollTop = Math.max(0, Math.min(bottom - box.clientHeight, top - REVEAL_CONTEXT))
}

/**
 * Open state and the height lock of one plot box. The height is the layout
 * height (`offsetHeight`, unaffected by the deck viewer's scale transform),
 * measured once on open: a container resize while open keeps the locked
 * height until the plot is closed.
 */
export function useOtherExpand(options: OtherExpandOptions = {}): OtherExpandState {
  const { growRoom } = options

  const [isOpen, setIsOpen] = useState(false)
  const [lock, setLock] = useState<{ height: number; room: number } | null>(null)
  const boxRef = useRef<HTMLDivElement>(null)
  const boxId = useId()

  const toggle = useCallback(() => {
    if (isOpen) {
      setIsOpen(false)
      setLock(null)
      return
    }
    const height = boxRef.current?.offsetHeight ?? 0
    const room = Math.max(0, Math.floor(growRoom?.() ?? 0))
    setLock(height > 0 ? { height, room } : null)
    setIsOpen(true)
  }, [isOpen, growRoom])

  useLayoutEffect(() => {
    if (isOpen && boxRef.current) {
      revealMembers(boxRef.current)
    }
  }, [isOpen])

  let boxStyle: React.CSSProperties | undefined
  if (isOpen && lock) {
    boxStyle =
      lock.room > 0
        ? { minHeight: lock.height, maxHeight: lock.height + lock.room, overflowY: 'auto', overscrollBehavior: 'contain' }
        : { height: lock.height, overflowY: 'auto', overscrollBehavior: 'contain' }
  }

  return { isOpen, toggle, boxRef, boxStyle, boxId }
}

export interface OtherToggleProps {
  state: OtherExpandState
  labels: OtherToggleLabels
  /** Every category the plot holds (drawn plus folded). */
  count: number
  /** The plot's test-id prefix: the link is `<prefix>-other-expand`, unique per plot. */
  testIdPrefix: string
  fontSize?: number
  style?: React.CSSProperties
}

const LINK = 'var(--slide-text-secondary, #525252)'

/** The text link that opens and closes Other. */
export function OtherToggle(props: OtherToggleProps) {
  const { state, labels, count, testIdPrefix, fontSize = 12, style } = props

  const text = state.isOpen ? labels.collapse : labels.expand(count)

  return (
    <button
      type="button"
      data-testid={`${testIdPrefix}-other-expand`}
      aria-expanded={state.isOpen}
      aria-controls={state.boxId}
      onClick={state.toggle}
      style={{
        alignSelf: 'flex-start',
        background: 'none',
        border: 0,
        padding: 0,
        margin: 0,
        cursor: 'pointer',
        font: 'inherit',
        fontSize,
        lineHeight: 1.3,
        color: LINK,
        textDecoration: 'underline',
        textUnderlineOffset: 2,
        ...style,
      }}
    >
      {text}
    </button>
  )
}
